<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { VForm } from 'vuetify/lib/components/index.mjs';
import { CustomerMgmtTypes } from '@ilteducation/customer-mgmt';
import { useComboboxFieldName, useComboboxName } from '../../composables/useComboboxName';
import { useLocale } from '../../composables/useLocale';
import { GradeSelect } from '@ilteducation/select/grade';
import { maxLength, minLength, required } from '../../rules';
import { useManageGroup } from '../../composables/useManageGroup';

const { t } = useLocale();
const { usedNames } = useManageGroup();

type GroupTypes = {
  value: CustomerMgmtTypes.GroupType;
  title: string;
};

const groupTypes: GroupTypes[] = [
  { value: 'class', title: t('lcp.class') },
  { value: 'subject', title: t('lcp.subject') },
];

const groupTypeMenuProps = useComboboxName(() => t('lcp.group_type'));
const expiryField = useComboboxFieldName(() => t('lcp.expiry_date'));

const usedNamesRule = (value: string) => (usedNames.value.includes(value) ? t('lcp.group_name_taken') : true);
const nameRules = [required(t), minLength(1, t), maxLength(255, t), usedNamesRule];

const formRef = ref<VForm | null>(null);

const name = ref('');
const type = ref<CustomerMgmtTypes.GroupType | null>(null);
const yearsInSchool = ref<number | undefined>(undefined);
const expiresAtInput = ref<Date | null>(null); // v-date-input model (picked day at local midnight)
// Exposed as an ISO UTC string at end-of-day (or null) for CreateGroupRequest.expiresAt.
const expiresAt = computed(() => {
  if (!expiresAtInput.value) return null;
  const d = new Date(expiresAtInput.value);
  d.setHours(23, 59, 59, 999);
  return d.toISOString();
});
// Earliest selectable expiry: start of today (so today is selectable).
const minExpiryDate = new Date();
minExpiryDate.setHours(0, 0, 0, 0);
watch(type, () => {
  yearsInSchool.value = yearsInSchool.value ?? undefined;
  formRef.value?.resetValidation();
});

const reset = () => {
  if (formRef.value) {
    formRef.value.reset();
    formRef.value.resetValidation();
  }
  name.value = '';
  type.value = groupTypes[0].value;
  yearsInSchool.value = undefined;
  expiresAtInput.value = null;
};

defineExpose({
  isValid: computed(() => formRef.value?.isValid || false),
  resetValidationAndValidate: () => {
    if (formRef.value) {
      formRef.value?.resetValidation();
      formRef.value?.validate();
    }
    return true;
  },
  name: name,
  type: type,
  yearsInSchool,
  expiresAt,
  groupTypes,
  reset,
});
</script>

<template>
  <v-form ref="formRef" class="d-flex flex-column ga-6">
    <v-text-field
      v-model="name"
      name="name"
      :label="t('lcp.group_name') + ' *'"
      :rules="nameRules"
      variant="outlined"
      block
      clearable
      clear-icon="mdi-close"
      :singleLine="false"
      hide-details="auto"
      bgColor="surface-bright"
    ></v-text-field>
    <v-select
      v-model="type"
      name="type"
      :items="groupTypes"
      :label="t('lcp.group_type')"
      :menu-props="groupTypeMenuProps"
      variant="outlined"
      density="default"
      hide-details="auto"
      bgColor="surface-bright"
      clearable
      clear-icon="mdi-close"
    ></v-select>
    <GradeSelect
      v-model="yearsInSchool"
      name="grade"
      :label="t('lcp.grade')"
      variant="outlined"
      density="default"
      hide-details="auto"
      bgColor="surface-bright"
      clearable
      clear-icon="mdi-close"
    ></GradeSelect>
    <v-date-input
      ref="expiryField"
      role="combobox"
      v-model="expiresAtInput"
      name="expiresAt"
      :label="t('lcp.expiry_date')"
      variant="outlined"
      density="default"
      prepend-icon=""
      append-inner-icon="mdi-calendar"
      hide-actions
      clearable
      clear-icon="mdi-close"
      :min="minExpiryDate"
      :hint="t('lcp.expiry_date_info')"
      persistent-hint
    ></v-date-input>
  </v-form>
</template>

<style scoped></style>
